import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type Mode = 'login' | 'signup';

export const LoginPage: React.FC = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('next') || '/';
  // ResetPasswordPage redirects here when the password was changed but the
  // follow-up auto-login did not complete. The reset itself succeeded.
  const passwordWasReset = searchParams.get('reset') === 'success';

  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState(searchParams.get('username') || '');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login({ username, password });
        navigate(redirectTo);
      } else {
        await register({ username, email, password, password_confirm: passwordConfirm, name });
        setRegistrationSuccess(true);
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: Record<string, unknown> }; message?: string };
      console.error(`${mode} error:`, error);

      if (error.response?.status === 500) {
        setError(`Server error (500): ${JSON.stringify(error.response?.data || 'Internal server error')}`);
      } else if (error.response?.status === 400) {
        const data = error.response?.data;
        if (mode === 'signup' && data && typeof data === 'object') {
          // Show friendly messages for common signup errors
          const emailErrors = Array.isArray(data.email) ? data.email.join(' ') : String(data.email || '');
          const usernameErrors = Array.isArray(data.username) ? data.username.join(' ') : String(data.username || '');
          if (emailErrors.toLowerCase().includes('already') || emailErrors.toLowerCase().includes('exists') || emailErrors.toLowerCase().includes('unique')) {
            setError('This email is already in use. Try signing in instead.');
          } else if (usernameErrors.toLowerCase().includes('already') || usernameErrors.toLowerCase().includes('exists') || usernameErrors.toLowerCase().includes('unique')) {
            setError('This username is already taken. Please choose another.');
          } else {
            // Fall through to generic field-level display
            const messages = Object.entries(data)
              .map(([field, errors]) => {
                const msgs = Array.isArray(errors) ? errors.join(', ') : String(errors);
                return `${field}: ${msgs}`;
              })
              .join('\n');
            setError(messages);
          }
        } else if (data && typeof data === 'object') {
          // Login mode: show field-level errors from DRF serializer
          const messages = Object.entries(data)
            .map(([field, errors]) => {
              const msgs = Array.isArray(errors) ? errors.join(', ') : String(errors);
              return `${field}: ${msgs}`;
            })
            .join('\n');
          setError(messages);
        } else {
          setError('Invalid request. Please check your inputs.');
        }
      } else if (error.response?.data?.detail) {
        setError(String(error.response.data.detail));
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError(`${mode === 'login' ? 'Login' : 'Registration'} failed. Check console for details.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName =
    'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded focus:outline-none focus:border-accent-yellow text-gray-100';

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg px-4">
      <div className="max-w-md w-full bg-dark-surface rounded-lg shadow-xl p-8">
        {/* The badge bar — one element carrying the monogram, the divider and
            the wordmark inside its gold pill, so nothing sits above or below
            repeating it. Same asset the top nav uses.

            Sized by WIDTH: at 283×52 it is a 5.4:1 strip, and it is vector, so
            it scales to any size without a larger file. `max-w-full` keeps it
            inside the card's padding on a narrow screen. */}
        <div className="flex justify-center mb-3">
          <img
            src="/logo-badge.svg"
            alt="Fuzion Trading"
            className="w-72 max-w-full h-auto"
          />
        </div>

        {/* Both former names: the rebrand ran wallstjesus → Steamroom → Fuzion,
            so a returning user may recognise either one and not the other. */}
        <p className="text-xs text-center text-gray-500 mb-6">
          formerly Steamroom Trading / Wall St. Jesus
        </p>

        {registrationSuccess ? (
          <div className="text-center py-8">
            <svg
              className="w-16 h-16 text-accent-yellow mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-100 mb-2">Account Created!</h3>
            <p className="text-sm text-gray-400 mb-6">
              Your account is ready. Sign in to continue.
            </p>
            <button
              type="button"
              onClick={() => {
                setRegistrationSuccess(false);
                setMode('login');
                setPassword('');
                setPasswordConfirm('');
                setEmail('');
                setName('');
                setError('');
              }}
              className="w-full bg-accent-yellow hover:bg-accent-yellow/85 text-dark-bg font-semibold py-3 rounded transition-colors"
            >
              Sign In
            </button>
          </div>
        ) : (
        <>
        <form onSubmit={handleSubmit} className="space-y-6">
          {passwordWasReset && !error && (
            <div className="bg-bull-blue/10 border border-bull-blue/30 text-bull-blue px-4 py-3 rounded text-sm">
              Your password has been changed. Please sign in with your new password.
            </div>
          )}

          {error && (
            <div className="bg-bear-red/10 border border-bear-red text-bear-red px-4 py-3 rounded whitespace-pre-line">
              {error}
            </div>
          )}

          <div>
            {/*
              Email works here as of portal #156, which replaced DRF's stock
              AuthTokenSerializer with a local one whose _lookup_user() resolves
              an address to its username before authenticate(). ACCOUNT_LOGIN_METHODS
              stays {"username"} and is not consulted — allauth's backend is not in
              this path.
            */}
            <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
              Username or email
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputClassName}
              required
              disabled={isLoading}
            />
          </div>

          {mode === 'signup' && (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClassName}
                  required
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClassName}
              required
              disabled={isLoading}
            />
            {mode === 'login' && (
              <div className="mt-1 text-right">
                <Link to="/reset-password" className="text-xs text-gray-400 hover:text-accent-yellow">
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          {mode === 'signup' && (
            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                id="passwordConfirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={inputClassName}
                required
                disabled={isLoading}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-accent-yellow hover:bg-accent-yellow/85 disabled:bg-gray-600 disabled:text-gray-400 text-dark-bg font-semibold py-3 rounded transition-colors"
          >
            {isLoading
              ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
              : (mode === 'login' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          {mode === 'login' ? (
            <>
              Don&apos;t have an account?{' '}
              <button type="button" onClick={switchMode} className="text-accent-yellow hover:underline">
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button type="button" onClick={switchMode} className="text-accent-yellow hover:underline">
                Sign in
              </button>
            </>
          )}
        </p>

        <div className="mt-8 pt-6 border-t border-dark-border">
          {/* ⚠️ Rewritten for the Steamroom → Fuzion move. The copy here
              previously described the wallstjesus → Steamroom migration, so it
              was a transition out of date and named tools that had long since
              stopped being new.

              Leads take Text Primary and bodies Text Muted, following the
              palette's own note that white is for titles. Gold is left alone —
              on this page it means "clickable", and none of this is. */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-[1.5px] mb-3">What&apos;s New</p>
          <ul className="space-y-2 text-sm text-gray-400">
            <li>
              <span className="text-white font-medium">A revamped platform</span>
              {' '}&mdash; Rebuilt interface with configurable dashboard layouts and a new look throughout
            </li>
            <li>
              <span className="text-white font-medium">Ticker Explorer</span>
              {' '}&mdash; Everything on one symbol: gamma levels, options flow, financials, earnings, analyst ratings, and congress and insider activity
            </li>
            <li>
              <span className="text-white font-medium">Market Overview</span>
              {' '}&mdash; The whole tape on one screen: breadth, sector rotation, index levels, movers, the day&apos;s largest flow and the biggest open interest changes
            </li>
            <li>
              <span className="text-white font-medium">Anti-VIX</span>
              {' '}&mdash; A volatility gauge to see when the VIX is overextended
            </li>
          </ul>
        </div>
        </>
        )}
      </div>
    </div>
  );
};
